import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UnmountClosed} from 'react-collapse';
import PropTypes from 'prop-types';
import {Button, Tooltip} from 'antd';
import {
  StyledVendorPaymentsList,
  StyledVendorPaymentsListTitle,
  StyledVendorPaymentsListSpace,
  StyledVendorPaymentsListItem,
  StyledVendorPaymentsListItemDate,
  StyledVendorPaymentsListItemSource,
  StyledVendorPaymentsListItemPriceSpace,
  StyledVendorPaymentsListItemIconButton
} from './StyledVendorPaymentsList';
import ImagePreviewTooltip from '../ImagePreviewTooltip';
import InvoiceAttachmentPreview from '../InvoiceAttachmentPreview';
import {amountHelpers, dateHelpers} from '../../../../utils/helpers';
import {EyeIcon} from '../../../../icons';

const VendorPaymentsList = ({
  attachments,
  onAttachmentLoad,
  payments,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [isOpen, setIsOpen] = useState(false);
  const [activeExpenseId, setActiveExpenseId] = useState(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);

  const finishAttachmentLoading = () => setAttachmentLoading(false);

  const invoiceAttachment = useMemo(() => {
    if (attachmentLoading) return `${t('Loading')}...`;

    const attachment = attachments.find(attachment => attachment.id === activeExpenseId);

    return (
      <InvoiceAttachmentPreview
        attachment={attachment}
      />
    );
  }, [attachments, attachmentLoading, activeExpenseId, t]);

  const handlePreview = (id) => {
    setActiveExpenseId(id);

    if (onAttachmentLoad) {
      setAttachmentLoading(true);
      onAttachmentLoad(id, finishAttachmentLoading);
    }
  }

  const getAmount = (payment, enabledEmpty = false) => {
    const isAttachmentUploaded = payment?.is_attachment_uploaded;
    return (
      <StyledVendorPaymentsListItemPriceSpace size='small'>
        <span className={enabledEmpty && !isAttachmentUploaded ? 'empty' : ''}>
          {amountHelpers.getAmountWithCurrencyCode(`-${payment.price}`)}
        </span>
        {isAttachmentUploaded && (
          <ImagePreviewTooltip
            classNames={{root: 'image-preview-tooltip-overlay transaction-details'}}
            trigger='click'
            title={invoiceAttachment}
          >
            <Tooltip title={t('preview')}>
              <StyledVendorPaymentsListItemIconButton
                onClick={() => handlePreview(payment.id)}
                icon={<EyeIcon />}
                size='small'
                type='text'
              />
            </Tooltip>
          </ImagePreviewTooltip>
        )}
      </StyledVendorPaymentsListItemPriceSpace>
    );
  }

  const getDate = (payment) => (
    <StyledVendorPaymentsListItemDate>
      {dateHelpers.getDateWithMonth(payment?.adding_date, 'DD MMM YYYY')}
    </StyledVendorPaymentsListItemDate>
  );

  const getSource = (payment) => (
    <StyledVendorPaymentsListItemSource>
      {payment?.vendor}
    </StyledVendorPaymentsListItemSource>
  );

  const toggleListButton = (
    <Button
      onClick={() => setIsOpen(!isOpen)}
      size='small'
      type='link'
    >
      {t(isOpen ? 'hide' : 'seeAll')}
    </Button>
  );

  const titleText = t('lastPaymentToThisVendor');

  return (
    <StyledVendorPaymentsList
      {...rest}
    >
      {isOpen ? (
        <StyledVendorPaymentsListTitle>
          <span>{titleText}</span>
          {toggleListButton}
        </StyledVendorPaymentsListTitle>
      ) : (
        <StyledVendorPaymentsListTitle>
          <span>{titleText} ({toggleListButton})</span>
          {payments.length && (
            <>
              {getDate(payments[0])}
              {getAmount(payments[0])}
            </>
          )}
        </StyledVendorPaymentsListTitle>
      )}

      <UnmountClosed isOpened={isOpen}>
        <StyledVendorPaymentsListSpace direction='vertical' size={10} className='vendor-payments-full-list'>
          {payments.map((payment, key) => (
            <StyledVendorPaymentsListItem key={key}>
              {getDate(payment)}
              {getSource(payment)}
              {getAmount(payment, true)}
            </StyledVendorPaymentsListItem>
          ))}
        </StyledVendorPaymentsListSpace>
      </UnmountClosed>
    </StyledVendorPaymentsList>
  );
};

VendorPaymentsList.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      contentType: PropTypes.string,
      data: PropTypes.object,
      id: PropTypes.string,
      file: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  onAttachmentLoad: PropTypes.func,
  payments: PropTypes.arrayOf(
    PropTypes.shape({
      adding_date: PropTypes.string,
      price: PropTypes.any,
      vendor: PropTypes.string
    })
  )
};

export default VendorPaymentsList;
